import { useQuery } from '@tanstack/react-query';
import { analyticsApi } from '../lib/api';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserGroupIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

interface DailyTrend {
  date: string;
  passed: number;
  failed: number;
  total: number;
}

interface ViolationCategory {
  category: string;
  count: number;
}

interface AnalyticsData {
  summary: {
    totalReports: number;
    reportsThisMonth: number;
    passed: number;
    failed: number;
    pending: number;
    errors: number;
  };
  approvals: {
    draft: number;
    pendingReview: number;
    approved: number;
    rejected: number;
  };
  teams: {
    count: number;
    totalMembers: number;
  };
  dailyTrend: DailyTrend[];
  topViolations: ViolationCategory[];
}

// Human-readable names for violation rule prefixes
const CATEGORY_LABELS: Record<string, string> = {
  UDAAP: 'UDAAP / FTC Act',
  FDIC: 'FDIC Part 328',
  NCUA: 'NCUA Part 740',
  REGZ: 'Reg Z (TILA)',
  REGDD: 'Reg DD (TISA)',
  FHA: 'Fair Housing Act',
  REGE: 'Reg E (EFTA)',
  ECOA: 'ECOA / Reg B',
};

export default function AnalyticsPage() {
  const { data, isLoading, isError, refetch } = useQuery<AnalyticsData>({
    queryKey: ['analytics-dashboard'],
    queryFn: async () => {
      const response = await analyticsApi.getDashboard();
      return response.data;
    },
    retry: 1,
  });

  if (isError) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8 text-center">
        <p className="text-red-600 font-medium mb-2">Failed to load data</p>
        <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-ocean-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-2">Unable to load analytics</h2>
        <p className="text-sm text-gray-500 mb-4">
          Analytics data could not be retrieved. Try uploading and scanning an ad first, then check back here.
        </p>
        <p className="text-xs text-gray-400">
          This dashboard shows your compliance scanning activity: total scans, pass rates, 30-day trends, approval pipeline status, and top violation categories.
        </p>
      </div>
    );
  }

  const { summary, approvals, teams, dailyTrend, topViolations } = data;

  // Find the max value in dailyTrend for bar scaling
  const maxDailyTotal = Math.max(...dailyTrend.map((d) => d.total), 1);

  // Pass rate
  const totalCompleted = summary.passed + summary.failed;
  const passRate = totalCompleted > 0 ? Math.round((summary.passed / totalCompleted) * 100) : 0;

  // Max violation count for scaling bars
  const maxViolationCount = Math.max(...topViolations.map((v) => v.count), 1);

  const statCards = [
    {
      name: 'Total Scans',
      value: summary.totalReports,
      icon: DocumentTextIcon,
      color: 'bg-ocean-100 text-ocean-600',
    },
    {
      name: 'This Month',
      value: summary.reportsThisMonth,
      icon: ChartBarIcon,
      color: 'bg-slate-100 text-slate-600',
    },
    {
      name: 'No Issues',
      value: summary.passed,
      icon: CheckCircleIcon,
      color: 'bg-mint-100 text-mint-600',
    },
    {
      name: 'Flagged for Review',
      value: summary.failed,
      icon: ExclamationTriangleIcon,
      color: 'bg-amber-100 text-amber-600',
    },
    {
      name: 'Pending',
      value: summary.pending,
      icon: ClockIcon,
      color: 'bg-gray-100 text-gray-600',
    },
    {
      name: 'Team Members',
      value: teams.totalMembers,
      icon: UserGroupIcon,
      color: 'bg-cyan-100 text-cyan-600',
    },
  ];

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-navy-900">Usage Analytics</h1>
        <p className="mt-2 text-gray-600">
          Track your compliance scanning activity and trends.
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {statCards.map((stat, i) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.color} mb-3`}>
              <stat.icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-navy-900">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-1">{stat.name}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* 30-Day Activity Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy-900 mb-1">30-Day Activity</h2>
          <p className="text-sm text-gray-500 mb-4">Scans per day over the last 30 days</p>

          <div className="flex items-end space-x-[2px] h-40">
            {dailyTrend.map((day) => {
              const totalHeight = maxDailyTotal > 0 ? (day.total / maxDailyTotal) * 100 : 0;
              const passedHeight = day.total > 0 ? (day.passed / day.total) * totalHeight : 0;
              const failedHeight = totalHeight - passedHeight;
              const dateObj = new Date(day.date + 'T00:00:00');
              const label = `${dateObj.getMonth() + 1}/${dateObj.getDate()}`;

              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-col items-center justify-end h-full group relative"
                >
                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                    <div className="bg-navy-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                      {label}: {day.total} scan{day.total !== 1 ? 's' : ''}
                    </div>
                  </div>
                  {/* Bar */}
                  {day.total > 0 ? (
                    <div
                      className="w-full flex flex-col rounded-t-sm overflow-hidden"
                      style={{ height: `${Math.max(totalHeight, 4)}%` }}
                    >
                      {failedHeight > 0 && (
                        <div
                          className="bg-amber-400 w-full"
                          style={{ height: `${(failedHeight / totalHeight) * 100}%` }}
                        />
                      )}
                      {passedHeight > 0 && (
                        <div
                          className="bg-mint-500 w-full flex-1"
                        />
                      )}
                    </div>
                  ) : (
                    <div className="w-full h-[2px] bg-gray-100 rounded" />
                  )}
                </div>
              );
            })}
          </div>
          {/* X-axis date labels — every 5 days, plus the first and last bar.
              Hovering still gives the exact date for any bar. */}
          <div className="flex space-x-[2px] mt-1">
            {dailyTrend.map((day, idx) => {
              const dateObj = new Date(day.date + 'T00:00:00');
              const label = `${dateObj.getMonth() + 1}/${dateObj.getDate()}`;
              const showLabel = idx === 0 || idx === dailyTrend.length - 1 || idx % 5 === 0;
              return (
                <div key={day.date} className="flex-1 text-center">
                  {showLabel && <span className="text-[10px] text-gray-400 tabular-nums">{label}</span>}
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center space-x-4 mt-4 text-xs text-gray-500">
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-sm bg-mint-500 mr-1" />
              No Issues
            </div>
            <div className="flex items-center">
              <div className="w-3 h-3 rounded-sm bg-amber-400 mr-1" />
              Flagged
            </div>
          </div>
        </div>

        {/* Pass Rate & Approval Status */}
        <div className="space-y-6">
          {/* Pass Rate */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-navy-900 mb-4">Compliance Pass Rate</h2>
            <div className="flex items-center">
              <div className="relative w-24 h-24 mr-6">
                <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                  <path
                    className="text-gray-200"
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <path
                    className={passRate >= 80 ? 'text-mint-500' : passRate >= 50 ? 'text-amber-500' : 'text-red-500'}
                    d="M18 2.0845
                      a 15.9155 15.9155 0 0 1 0 31.831
                      a 15.9155 15.9155 0 0 1 0 -31.831"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeDasharray={`${passRate}, 100`}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xl font-bold text-navy-900">{passRate}%</span>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                <p><strong>{summary.passed}</strong> of {totalCompleted} completed scans had no issues.</p>
                {totalCompleted === 0 && (
                  <p className="mt-1 text-gray-400">Upload ads to see your pass rate.</p>
                )}
              </div>
            </div>
          </div>

          {/* Approval Pipeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-navy-900 mb-4">Approval Pipeline</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Draft', value: approvals.draft, activeColor: 'text-gray-600' },
                { label: 'Pending Review', value: approvals.pendingReview, activeColor: 'text-amber-600' },
                { label: 'Approved', value: approvals.approved, activeColor: 'text-mint-600' },
                { label: 'Rejected', value: approvals.rejected, activeColor: 'text-red-600' },
              ].map((item) => (
                <div key={item.label}>
                  <p className={`text-2xl font-bold ${item.value > 0 ? item.activeColor : 'text-gray-900'}`}>{item.value}</p>
                  <p className="text-xs text-gray-500">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Top Violations */}
      {topViolations.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-navy-900 mb-1">Top Violation Categories</h2>
          <p className="text-sm text-gray-500 mb-4">Most commonly flagged rule categories across your scans</p>

          <div className="space-y-3">
            {topViolations.map((v) => {
              const widthPercent = (v.count / maxViolationCount) * 100;
              const label = CATEGORY_LABELS[v.category] || v.category;
              return (
                <div key={v.category} className="flex items-center">
                  <div className="w-36 text-sm text-gray-700 font-medium truncate mr-4">
                    {label}
                  </div>
                  <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-ocean-500 rounded-full transition-all duration-500"
                      style={{ width: `${widthPercent}%` }}
                    />
                  </div>
                  <div className="w-12 text-right text-sm font-semibold text-navy-900 ml-3">
                    {v.count}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
